import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Switch from '../../../components/ui/Switch';
import type { ProfilePersonaView, ProfilePersonaInput } from '../../../api/PublicProfile/publicProfileApi';

interface EditPersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (personaId: number, input: ProfilePersonaInput) => void;
  persona: ProfilePersonaView | null;
}

interface FormData {
  name: string;
  description: string;
  isDefault: boolean;
  requiresApproval: boolean;
  allowSlugRegeneration: boolean;
}

interface Errors {
  name?: string;
  description?: string;
}

const EditPersonaModal: React.FC<EditPersonaModalProps> = ({ isOpen, onClose, onEdit, persona }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isDefault: false,
    requiresApproval: false,
    allowSlugRegeneration: true,
  });

  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (persona) {
      setFormData({
        name: persona.name || '',
        description: persona.description || '',
        isDefault: persona.isDefault || false,
        requiresApproval: persona.requiresApproval || false,
        allowSlugRegeneration: persona.allowSlugRegeneration ?? true,
      });
    }
  }, [persona]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field as keyof Errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = t('Persona name is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (validateForm() && persona) {
      const input: ProfilePersonaInput = {
        name: formData.name,
        description: formData.description || undefined,
        isDefault: formData.isDefault,
        requiresApproval: formData.requiresApproval,
        allowSlugRegeneration: formData.allowSlugRegeneration,
      };

      onEdit(persona.id, input);
      setErrors({});
      onClose();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent size="default">
        <DialogHeader>
          <DialogTitle>{t('Edit Persona')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Persona Name')} *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder={t('Enter persona name')}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              {errors?.name && (
                <p className="text-sm text-destructive mt-1">{errors?.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Description')}
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder={t('Enter persona description')}
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>

            {/* Toggle Options */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => handleInputChange('isDefault', checked)}
                  label={t('Set as Default')}
                  description={t('New profiles will use this persona by default')}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) => handleInputChange('requiresApproval', checked)}
                  label={t('Requires HR Approval')}
                  description={t('Profiles must be approved before going public')}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.allowSlugRegeneration}
                  onCheckedChange={(checked) => handleInputChange('allowSlugRegeneration', checked)}
                  label={t('Allow Link Regeneration')}
                  description={t('Users can regenerate their profile URL')}
                />
              </div>
            </div>

            {/* Profile Count Info */}
            {persona && persona.profileCount > 0 && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <Info className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                <p className="text-xs text-warning">
                  {t('This persona is currently used by {{count}} profile(s). Changes will affect all profiles using this persona.', { count: persona.profileCount })}
                </p>
              </div>
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            {t('Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPersonaModal;
